import type { FetchQueryParams } from '@/components/paginated-datatable/types/paginatedDataTableTypes';
import {
  type CreatePointOfSaleToAreaV1Dto,
  type CreatePointOfSaleV1Dto,
  OrderByRelationalPropertyDto,
  type PatchPointOfSaleToAreaV1Dto,
  type PatchPointOfSaleV1Dto,
  type PointOfSaleDetailV1Dto,
  type PointOfSaleFilterInnerV2Dto,
  PointOfSalesService,
  PointOfSaleToAreasService,
  type PointOfSaleToAreaV1Dto,
  type PointOfSaleV1Dto,
} from '@/openapi';
import { eventBus } from '@/configs/eventBus';

import type {
  PointOfSaleDetailSimple,
  PointOfSaleSimple,
  PointOfSaleToAreaSimple,
} from '../pointOfSalesTypes';

export function usePointOfSales() {
  const fetchPointOfSale = async (id: string) => {
    try {
      return await PointOfSalesService.pointOfSaleControllerV1FindOneV1(id);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPointOfSales = async ({
    orderBy,
    page,
    limit,
    filter,
    searchString,
  }: FetchQueryParams<PointOfSaleFilterInnerV2Dto>) => {
    try {
      return PointOfSalesService.pointOfSaleControllerV2FindAllV2(
        orderBy,
        page,
        limit,
        filter,
        searchString,
      );
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPointOfSalesSimple = async (
    orderBy?: Array<OrderByRelationalPropertyDto> | null,
    filter?: PointOfSaleFilterInnerV2Dto | null,
    searchString?: string | null,
    page?: number,
    limit?: number,
    contractId?: string,
  ) => {
    try {
      return PointOfSalesService.pointOfSaleControllerV2FindAllSimplifiedV2(
        orderBy,
        filter,
        searchString,
        page,
        limit,
        contractId,
      );
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPointOfSaleToAreaByPointOfSaleId = async (id: string) => {
    try {
      return await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1FindManyByPointOfSaleIdV1(
        id,
      );
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPointOfSaleToAreaById = async (id: string) => {
    try {
      return await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1FindOneV1(id);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPointOfSalesByContractId = async (contractId: string) => {
    try {
      return PointOfSalesService.pointOfSaleControllerV1FindByContractIdV1(contractId);
    } catch (err) {
      console.log(err);
    }
  };

  const createPointOfSale = async (data: CreatePointOfSaleV1Dto) => {
    try {
      await PointOfSalesService.pointOfSaleControllerV1CreateV1(data);
      eventBus.emit('create');
    } catch (err) {
      console.log(err);
    }
  };

  const createPointOfSaleToArea = async (data: CreatePointOfSaleToAreaV1Dto) => {
    try {
      await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1CreateV1(data);
      eventBus.emit('create');
    } catch (err) {
      console.log(err);
    }
  };

  const updatePointOfSale = async (id: string, data: PatchPointOfSaleV1Dto) => {
    try {
      await PointOfSalesService.pointOfSaleControllerV1UpdatePartialV1(id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  const updatePointOfSaleToArea = async (id: string, data: PatchPointOfSaleToAreaV1Dto) => {
    try {
      await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1UpdatePartialV1(id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  const deletePointOfSale = async (id: string) => {
    try {
      await PointOfSalesService.pointOfSaleControllerV1RemoveOneV1(id);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deletePointOfSaleOnArea = async (posToAreaId: string) => {
    try {
      await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1RemoveOneV1(posToAreaId);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deletePointOfSales = async (ids: string[]) => {
    try {
      await PointOfSalesService.pointOfSaleControllerV1RemoveManyV1({ values: ids });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deleteManyPointOfSaleOnArea = async (ids: string[]) => {
    try {
      await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1RemoveManyV1({ values: ids });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  return {
    fetchPointOfSale,
    fetchPointOfSales,
    fetchPointOfSalesSimple,
    fetchPointOfSaleToAreaByPointOfSaleId,
    fetchPointOfSaleToAreaById,
    fetchPointOfSalesByContractId,
    createPointOfSale,
    createPointOfSaleToArea,
    updatePointOfSale,
    updatePointOfSaleToArea,
    deletePointOfSale,
    deletePointOfSaleOnArea,
    deletePointOfSales,
    deleteManyPointOfSaleOnArea,
  };
}

export function usePointOfSalesConverter() {
  const convertPointOfSaleDetailDtoToPointOfSaleDetailSimple = (
    data: PointOfSaleDetailV1Dto,
  ): PointOfSaleDetailSimple => {
    const {
      createdAt: _createdAt,
      modifiedAt: _modifiedAt,
      customer: _customer,
      addresses: _addresses,
      ...res
    } = data;
    return { ...res, customerId: data.customer.id };
  };

  const convertPointOfSaleDtoToPointOfSaleSimple = (data: PointOfSaleV1Dto): PointOfSaleSimple => {
    const {
      createdAt: _createdAt,
      modifiedAt: _modifiedAt,
      customer: _customer,
      addresses: _addresses,
      tickets: _tickets,
      ...res
    } = data;
    return { ...res, customerId: data.customer.id };
  };

  const convertPointOfSaleDtoListToPointOfSaleSimpleList = (
    data: PointOfSaleV1Dto[],
  ): PointOfSaleSimple[] => {
    return data.map(convertPointOfSaleDtoToPointOfSaleSimple);
  };

  const convertPointOfSaleDetailSimpleToCreatePointOfSaleDto = (
    data: PointOfSaleDetailSimple,
  ): CreatePointOfSaleV1Dto => {
    const {
      version: _version,
      id: _id,
      tickets: _tickets,
      areas: _areas,
      contracts: _contracts,
      conditions: _conditions,
      operatingCostInvoices: _operatingCostInvoices,
      documents: _documents,
      ...res
    } = data;
    return res;
  };

  const convertPointOfSaleDetailSimpleToPatchPointOfSaleDto = (
    data: PointOfSaleDetailSimple,
  ): PatchPointOfSaleV1Dto => {
    const {
      id: _id,
      tickets: _tickets,
      areas: _areas,
      contracts: _contracts,
      conditions: _conditions,
      operatingCostInvoices: _operatingCostInvoices,
      documents: _documents,
      ...res
    } = data;
    return res;
  };

  const convertPointOfSaleToAreaDtoToPointOfSaleToAreaSimple = (
    data: PointOfSaleToAreaV1Dto,
  ): PointOfSaleToAreaSimple => {
    const { createdAt: _createdAt, modifiedAt: _modifiedAt, ...res } = data;
    return { ...res };
  };

  const convertPointOfSaleToAreaSimpleToCreatePointOfSaleToAreaDto = (
    data: PointOfSaleToAreaSimple,
  ): CreatePointOfSaleToAreaV1Dto => {
    const {
      id: _id,
      version: _version,
      pointOfSaleName: _pointOfSaleName,
      areaName: _areaName,
      ...res
    } = data;
    return { ...res };
  };

  const convertPointOfSaleToAreaSimpleToUpdatePointOfSaleToAreaDto = (
    data: PointOfSaleToAreaSimple,
  ): PatchPointOfSaleToAreaV1Dto => {
    const { id: _id, pointOfSaleName: _pointOfSaleName, areaName: _areaName, ...res } = data;
    return { ...res };
  };

  return {
    convertPointOfSaleDetailDtoToPointOfSaleDetailSimple,
    convertPointOfSaleDtoToPointOfSaleSimple,
    convertPointOfSaleDtoListToPointOfSaleSimpleList,
    convertPointOfSaleDetailSimpleToCreatePointOfSaleDto,
    convertPointOfSaleDetailSimpleToPatchPointOfSaleDto,
    convertPointOfSaleToAreaDtoToPointOfSaleToAreaSimple,
    convertPointOfSaleToAreaSimpleToCreatePointOfSaleToAreaDto,
    convertPointOfSaleToAreaSimpleToUpdatePointOfSaleToAreaDto,
  };
}
